import React, { useEffect, useState } from 'react'
import {
  GridCell,
  Modal,
  InputText,
  Loading,
  Radio,
  RadioGroup,
  Dropdown,
  Spacing,
} from 'components'
import { TopSection, Typography } from '@evrekadev/evreka-ui-components'
import { useTranslation } from 'utils/useTranslation'
import { useMapSetting } from 'utils/useMapSetting'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { ToggleGroup } from './ToggleGroup'
import { MapSettingsOptions } from 'components/MapV2/types'
import { useMapContext } from 'components/MapV2/MapProvider'
import { MapSettingsType } from './types'

export type MapSettingsModalProps = {
  isOpen: boolean
  onClose: () => void
}

export const MapSettingsModal: React.FC<React.PropsWithChildren<MapSettingsModalProps>> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('components.map.settings')

  const { settings } = useMapContext()

  const [columnSettings, setColumnSettings] = useState<MapSettingsOptions>([])

  const {
    mapSettingProps: { onSettingChange, mapSetting: mapSettingsStorage },
  } = useMapSetting(settings?.key)

  const methods = useForm()

  useEffect(() => {
    if (!columnSettings.length) {
      const data = mapSettingsStorage

      if (data && settings) {
        settings.onChange(data)
        setColumnSettings(data)
      }
    }
  }, [mapSettingsStorage, settings])

  const onSubmit = (data: any) => {
    settings?.onChange(data)

    if (onSettingChange) {
      onSettingChange(data)
    }

    onClose()
  }

  useEffect(() => {
    methods.reset(mapSettingsStorage)
  }, [])

  return (
    <Modal
      componentSize="medium"
      isOpen={isOpen}
      onRequestClose={onClose}
      heading={t('title')}
      formProps={{ onSubmit: methods.handleSubmit(onSubmit) }}
    >
      <FormProvider {...methods}>
        <SettingsForm settings={settings} />
      </FormProvider>
    </Modal>
  )
}

const SettingsForm: React.FC<{ settings?: MapSettingsType }> = ({ settings }) => {
  const { control } = useFormContext()

  if (!settings) {
    return <Loading componentSize={24} type="spinner" />
  }

  return settings.forms?.map((form) => {
    if (form.type === 'HEADER') {
      return (
        <Spacing my={8} key={form.id + form.title}>
          <Typography fontWeight="semibold">{form.title}</Typography>
        </Spacing>
      )
    }
    if (form.type === 'DROPDOWN') {
      return (
        <GridCell key={form.id}>
          <Controller
            render={({ field }) => (
              <Dropdown
                {...field}
                isClearable
                label={form.title}
                labelRequired
                options={form.options}
              />
            )}
            control={control}
            name={form.id}
          />
        </GridCell>
      )
    }
    if (form.type === 'INPUT') {
      return (
        <GridCell key={form.id}>
          <Controller
            control={control}
            name={form.id}
            defaultValue={form.defaultValue}
            render={({ field: { ...rest } }) => <InputText {...rest} label={form.title} />}
          />
        </GridCell>
      )
    }
    if (form.type === 'RADIO_GROUP') {
      return (
        <GridCell key={form.id}>
          <TopSection label={form.title}></TopSection>
          <Controller
            render={({ field: { onChange, value } }) => (
              <RadioGroup
                sideBySide={form.isInline}
                value={typeof value === 'string' ? value : value?.value}
                onChange={(e) => {
                  onChange(form.options?.find((o) => o.value === e.target.value))
                }}
              >
                {form.options?.map((option) => (
                  <Radio key={option.value} value={option.value} label={option.label}></Radio>
                ))}
              </RadioGroup>
            )}
            control={control}
            name={form.id}
            defaultValue={form.defaultValue || form.options?.[0].value}
          />
        </GridCell>
      )
    }
    if (form.type === 'TOGGLE_GROUP') {
      return (
        <GridCell key={form.id}>
          <Controller
            control={control}
            name={form.id}
            defaultValue={form.defaultValue}
            render={({ field: { ...rest } }) => <ToggleGroup {...rest} options={form.options} />}
          />
        </GridCell>
      )
    }
    return null
  })
}
